/**
 * `AppHttpClient` — THE browser HTTP client layer for app code (spec
 * customer-success/v1.5.1/002 §2.1-§2.2).
 *
 * It is `FetchHttpClient.layer` with a request mapping that adds the scope's
 * headers (`scopeRequestHeaders`): the `x-cova-org` claim for the firm this tab
 * acts as in that scope. No scope header: the server takes the scope from the
 * acting firm (022 G48b, WP3.17.2a-b2). The scope is read PER REQUEST off the fiber
 * that makes it (`RequestScope`, set by `withRequestScope`), never off a module
 * value: the query runner (`createEffectQueryNoAbort`) sets it from the
 * `QueryClient` the query runs on, which is the nearest
 * `PlatformScopeProvider`'s (FIX-SCOPE-STORE). A request made with no scope on
 * its fiber falls back to the ROUTE's scope: the COMMITTED section scope
 * (`platform-scope-store.ts`, written only at commit and reset on unmount) when
 * a section provider is mounted, else `scopeForPath` of the tab's path (U-4).
 * That fallback serves the imperative `Effect.runPromise` and atom runs not yet
 * carrying a scope. Runs whose route alone cannot name the right scope are
 * listed, each with its reason, in
 * `src/__tests__/boundaries/request-scope-allowlist.json`, and the ratchet
 * beside it fails on a new one (FIX-SCOPE-STORE-2).
 *
 * WHY ONE LAYER AND NOT 260 `transformClient` CALLS. 240 files under
 * `src/features`, `src/entities` and `src/lib` build their own
 * `RpcClient.layerProtocolHttp({ url })` and provide `FetchHttpClient.layer` to
 * it. Threading a per-layer transform through all of them would be 240 hand
 * edits with nothing to stop the 241st file forgetting. Replacing the `Fetch`
 * service inside one shared layer is a single definition, and the sweep that
 * points every call site at it is ratcheted by
 * `src/__tests__/boundaries/browser-http-client.test.ts`.
 *
 * SERVER CODE KEEPS `FetchHttpClient`. `src/contexts`, `src/infrastructure`,
 * `src/lib/tracing`, `src/lib/test` and the test suites are outside the sweep:
 * they have no scope store to read and no header to send.
 *
 * THE BROWSER TRACER RIDES HERE TOO (spec D14). Without
 * `EffectBrowserTracerLayer`, `RpcClient.<tag>` and `http.client` spans come
 * from Effect's NATIVE tracer, which nothing exports — yet `HttpClient` still
 * writes their ids into `traceparent` as a plain header object, and
 * `FetchInstrumentation` lets a caller's header win over its own. The server
 * tree then hangs off a span SigNoz never receives, and the browser's
 * `HTTP POST` span sits alone in an unrelated trace. Only three of ~300 client
 * layers merged the bridge by hand; every client provides this layer, so the
 * bridge goes here once. Inert without a tracer provider (no consent, or
 * tracing unset): propagation is switched off and the server span is the root.
 * Pinned by `src/lib/tracing/__tests__/browser-telemetry-linking.test.ts`.
 *
 * THE CLIENT NAMES ITSELF HERE TOO (005 API-8 as amended by R0-32): every
 * request carries `x-cova-client: platform:web:<version>`, for telemetry only
 * (011 `cova.client`); no security rule reads it. One layer, so every RPC
 * client the browser builds sends it. The version is pinned to the root
 * package.json, and the value to the platform's allowlist, by
 * `src/__tests__/boundaries/cova-client-contract.test.ts`.
 */
import { FetchHttpClient, HttpClient, HttpClientRequest } from "@effect/platform"
import { Effect, FiberRef, Layer, Option } from "effect"
import { DEFAULT_PLATFORM_SCOPE, scopeForPath, type PlatformScope } from "@/domain/scope/platform-scope"
import { COVA_CLIENT_HEADER } from "@/lib/tracing/cova-attribution"
import { EffectBrowserTracerLayer } from "@/lib/tracing/effect-browser-tracer"
import { OWN_WRITES_HEADER, noteOwnWrites } from "@/shared/lib/live-invalidation/own-writes"
import { getCurrentScope } from "./platform-scope-store"
import { scopeRequestHeaders, withViewAs } from "./scoped-fetch"

export const PLATFORM_WEB_CLIENT_VERSION = "3.0.0"

export const PLATFORM_WEB_CLIENT_ID = `platform:web:${PLATFORM_WEB_CLIENT_VERSION}`

type HeadersFn = () => Readonly<Record<string, string>>

/**
 * A host header function (`AgentHost.rpc.headers`) that also names the
 * platform web client. The agent chat surface builds its own HTTP client
 * inside `@cova/agent-v2-ui`, so it does not ride this layer; its hosts wrap
 * their firm claim in this instead. Read per call, like the claim it wraps.
 */
export const withPlatformWebClient =
  (headers: HeadersFn): HeadersFn =>
  () => ({ [COVA_CLIENT_HEADER]: PLATFORM_WEB_CLIENT_ID, ...headers() })

/**
 * The scope the running fiber's requests are for. `None` = the caller named
 * none, so `AppHttpClient.layer` uses the committed section scope. A fiber
 * forked from the caller inherits it, which is how it reaches the RPC
 * protocol's send.
 */
export const RequestScope: FiberRef.FiberRef<Option.Option<PlatformScope>> = FiberRef.unsafeMake<
  Option.Option<PlatformScope>
>(Option.none())

/** Run `self` with every `AppHttpClient.layer` request in it tagged for `scope`. */
export const withRequestScope =
  (scope: PlatformScope) =>
  <A, E, R>(self: Effect.Effect<A, E, R>): Effect.Effect<A, E, R> =>
    Effect.locally(self, RequestScope, Option.some(scope))

/**
 * THE FALLBACK FOLLOWS THE ROUTE (U-4, D-F4; the same fact M8b keys the root
 * `QueryClient` by). A run that named no scope claims the scope of the surface
 * the tab is on: the committed section scope when a section provider is
 * mounted, else the scope the tab's path names (`scopeForPath`). Without the
 * second half, `/super-admin` (a Cova Staff section with no provider of its own,
 * STAFF-G5b) sent the brokerage firm on every imperative run. Read per request
 * off `location`, so nothing writes the store but its one writer. No `location`
 * (server, tests in node) reads as `/`, the broker default.
 */
const routeScope = (): PlatformScope => {
  const committed = getCurrentScope()
  if (committed !== DEFAULT_PLATFORM_SCOPE) return committed
  return scopeForPath(globalThis.location?.pathname ?? "/")
}

const requestScopeFromFiber: Effect.Effect<PlatformScope> = Effect.map(
  FiberRef.get(RequestScope),
  Option.getOrElse(routeScope),
)

/**
 * The `Fetch` every app HTTP client sends through: the CRM viewer's delegate
 * (`withViewAs`) over `globalThis.fetch`, read per call so the MFA interceptor
 * stays in the path. The scope's claim is set by the request mapping below, so
 * the delegate's base adds none.
 */
const viewAwareFetch: typeof fetch = withViewAs((input, init) =>
  globalThis.fetch(input, init).then((response) => {
    // A BMS write's transaction ids (query speed Q11): noted so the change
    // feed's poller can name this tab's own saves. Absent on every other answer.
    noteOwnWrites(response.headers.get(OWN_WRITES_HEADER))
    return response
  }),
)

/** `FetchHttpClient` over the view-aware `Fetch`, plus the client's and the scope's headers. */
const tagging = (scopeOf: Effect.Effect<PlatformScope>) =>
  Layer.effect(
    HttpClient.HttpClient,
    Effect.map(
      HttpClient.HttpClient,
      HttpClient.mapRequestEffect((request) =>
        Effect.map(scopeOf, (scope) =>
          HttpClientRequest.setHeaders(request, { [COVA_CLIENT_HEADER]: PLATFORM_WEB_CLIENT_ID, ...scopeRequestHeaders(scope) }),
        ),
      ),
    ),
  ).pipe(
    Layer.provide(FetchHttpClient.layer),
    Layer.provide(Layer.succeed(FetchHttpClient.Fetch, viewAwareFetch)),
    Layer.merge(EffectBrowserTracerLayer),
  )

/**
 * `layerForScope` — the same layer, pinned to ONE product: it ignores the
 * fiber's `RequestScope` (R-1).
 *
 * `layer` is right for every per-surface query. `layerForScope` is for the
 * caller whose CACHE KEY is a scope — the identity read, and the root
 * providers keyed by the route (`scopedProtocolLayer`): the request must carry
 * the scope its key names, whichever client runs it.
 *
 * It lives here rather than in the identity slice because this module is the
 * one place allowed to name `FetchHttpClient` on the browser side (the sweep
 * ratchet `browser-http-client.test.ts` bans it everywhere else, and rightly:
 * naming it usually means an unscoped client).
 *
 * Memoised per scope — the layer is stateless, but a fresh one per render would
 * rebuild the RPC client with it.
 */
const byScope = new Map<PlatformScope, ReturnType<typeof tagging>>()

export const AppHttpClient = {
  layer: tagging(requestScopeFromFiber),
  layerForScope: (scope: PlatformScope) => {
    const existing = byScope.get(scope)
    if (existing !== undefined) return existing
    const layer = tagging(Effect.succeed(scope))
    byScope.set(scope, layer)
    return layer
  },
} as const
